import React, { createContext, useCallback, useContext, useMemo } from "react";

import { CURRENT_EVENT_SLUG } from "src/shared/constants/event";
import { Nullable } from "src/shared/utils/typescript";

import { useUserContext } from "../UserContext";

import { useGetDiscoveryProfileByUserQuery } from "./graphql/getDiscoveryProfileByUser.generated";
import {
  UpsertDiscoveryProfileMutationVariables,
  useUpsertDiscoveryProfileMutation,
} from "./graphql/upsertDiscoveryProfile.generated";

export type DiscoveryProfile = {
  claimId: number;
  name: Nullable<string>;
  pronouns: Nullable<string[]>;
  profile_photo: Nullable<string>;
  profile_photo_caption: Nullable<string>;
  bio_prompt: Nullable<string>;
  bio: Nullable<string>;
  interests: Nullable<string[]>;
  experience_goals: Nullable<string>;
  weekend_pace: Nullable<number>;
  skills: Nullable<string[]>;
  teammates_needed: Nullable<number>;
  teammate_criteria: Nullable<string>;
  teammates_claim_ids: Nullable<string[]>;
  contact_email: Nullable<string>;
  contact_discord: Nullable<string>;
  contact_linkedin: Nullable<string>;
  contact_instagram: Nullable<string>;
  contact_x: Nullable<string>;
  contact_phone: Nullable<string>;
  isPublished: boolean;
};

enum DiscoveryProfileStage {
  DRAFT = "draft",
  PUBLISHED = "published",
}

export interface ProfileContextState {
  profile: DiscoveryProfile | null;
  isLoading: boolean;
  loadError?: string;
  isUpdating: boolean;
  updateError?: string;
  upsertDiscoveryProfile: (
    vars: Omit<UpsertDiscoveryProfileMutationVariables, "stage_slug"> & {
      stage_slug?: string;
    }
  ) => Promise<unknown>;
  autosaveDiscoveryProfile: (
    vars: Omit<UpsertDiscoveryProfileMutationVariables, "stage_slug">
  ) => Promise<unknown>;
  refetch: () => Promise<unknown> | void;
}

const DEFAULT_STATE: ProfileContextState = {
  profile: null,
  isLoading: false,
  isUpdating: false,
  upsertDiscoveryProfile: () => {
    throw new Error("No parent ProfileContext found");
  },
  autosaveDiscoveryProfile: () => {
    throw new Error("No parent ProfileContext found");
  },
  refetch: () => {},
};

const ProfileContext = createContext<ProfileContextState>(DEFAULT_STATE);
export const useProfileContext = () => useContext(ProfileContext);

export const ProfileContextProvider: React.FC = ({ children }) => {
  const { id } = useUserContext();

  const { data, loading, error, refetch } = useGetDiscoveryProfileByUserQuery({
    variables: {
      userId: id as number,
      eventSlug: CURRENT_EVENT_SLUG,
    },
    skip: !id,
  });

  const [upsertMutation, { loading: upserting, error: upsertError }] =
    useUpsertDiscoveryProfileMutation();
  const [autosaveMutation] = useUpsertDiscoveryProfileMutation({
    fetchPolicy: "no-cache",
  });

  const claim = data?.claims[0];

  // Stable identity so edit forms don't reset staging when unrelated
  // context state changes (e.g. upsert loading) recreate this object.
  const profile: DiscoveryProfile | null = useMemo(() => {
    if (!claim) return null;
    const fieldMap = Object.fromEntries(
      claim.fields.map((field) => [field.slug, field.answer?.value])
    );
    return {
      claimId: claim.id,
      name: fieldMap.name as string | undefined,
      pronouns: fieldMap.pronouns as string[] | undefined,
      profile_photo: fieldMap.profile_photo as string | undefined,
      profile_photo_caption: fieldMap.profile_photo_caption as
        | string
        | undefined,
      bio_prompt: fieldMap.bio_prompt as string | undefined,
      bio: fieldMap.bio as string | undefined,
      interests: fieldMap.interests as string[] | undefined,
      experience_goals: fieldMap.experience_goals as string | undefined,
      weekend_pace: fieldMap.weekend_pace as number | undefined,
      skills: fieldMap.skills as string[] | undefined,
      teammates_needed: fieldMap.teammates_needed as number | undefined,
      teammate_criteria: fieldMap.teammate_criteria as string | undefined,
      teammates_claim_ids: fieldMap.teammates_claim_ids as string[] | undefined,
      contact_email: fieldMap.contact_email as string | undefined,
      contact_discord: fieldMap.contact_discord as string | undefined,
      contact_linkedin: fieldMap.contact_linkedin as string | undefined,
      contact_instagram: fieldMap.contact_instagram as string | undefined,
      contact_x: fieldMap.contact_x as string | undefined,
      contact_phone: fieldMap.contact_phone as string | undefined,
      isPublished: claim.stage.slug === DiscoveryProfileStage.PUBLISHED,
    };
  }, [claim]);

  const handleRefetch = useCallback(async () => {
    if (id) {
      return refetch({ userId: id, eventSlug: CURRENT_EVENT_SLUG });
    }
    return undefined;
  }, [id, refetch]);

  const upsertDiscoveryProfile = useCallback(
    async (
      vars: Omit<UpsertDiscoveryProfileMutationVariables, "stage_slug"> & {
        stage_slug?: string;
      }
    ) => {
      if (!id) throw new Error("Undefined user id");
      const result = await upsertMutation({
        variables: {
          ...vars,
          stage_slug: vars.stage_slug ?? DiscoveryProfileStage.DRAFT,
        },
      });
      await handleRefetch();
      return result;
    },
    [id, upsertMutation, handleRefetch]
  );

  const autosaveDiscoveryProfile = useCallback(
    async (
      vars: Omit<UpsertDiscoveryProfileMutationVariables, "stage_slug">
    ) => {
      if (!id) throw new Error("Undefined user id");
      return autosaveMutation({
        variables: {
          ...vars,
          stage_slug: DiscoveryProfileStage.DRAFT,
        },
      });
    },
    [id, autosaveMutation]
  );

  const state: ProfileContextState = {
    profile,
    isLoading: loading,
    loadError: error?.message,
    isUpdating: upserting,
    updateError: upsertError?.message,
    upsertDiscoveryProfile,
    autosaveDiscoveryProfile,
    refetch: handleRefetch,
  };

  return (
    <ProfileContext.Provider value={state}>{children}</ProfileContext.Provider>
  );
};
